const DAILY_GOAL_SECONDS = 15 * 60
const MONDAY_WEEK_START = 1
const WEEKDAY_VALUES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

export interface LearningActivityRecord {
  readonly createdAt: number
  readonly seconds?: number
}

export interface LearningActivityProfileSource {
  readonly daily_activities?: readonly LearningActivityRecord[] | null
  readonly my_daily_goal?: readonly string[] | null
}

export interface LearningActivityMetrics {
  readonly dailyStudiedSeconds: number
  readonly todayTargetSeconds: number
  readonly weeklyGoalProgressPercent: number
  readonly weeklyStudiedSeconds: number
  readonly weeklyTargetSeconds: number
}

export interface WeekBounds {
  readonly endExclusive: Date
  readonly startInclusive: Date
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function getLocalDayStart(date: Date) {
  const dayStart = new Date(date)
  dayStart.setHours(0, 0, 0, 0)

  return dayStart
}

function getNextLocalDayStart(date: Date) {
  const nextDayStart = getLocalDayStart(date)
  nextDayStart.setDate(nextDayStart.getDate() + 1)

  return nextDayStart
}

function getProfileActivities(profile: LearningActivityProfileSource | null) {
  return Array.isArray(profile?.daily_activities)
    ? profile.daily_activities
    : []
}

function getActivityCreatedAt(activity: LearningActivityRecord) {
  const createdAt = new Date(activity.createdAt)

  if (Number.isNaN(createdAt.getTime())) {
    return null
  }

  return createdAt
}

function getActivitySeconds(activity: LearningActivityRecord) {
  if (!isFiniteNumber(activity.seconds)) {
    return 0
  }

  return Math.max(0, activity.seconds)
}

function getSelectedGoalDays(profile: LearningActivityProfileSource | null) {
  if (!Array.isArray(profile?.my_daily_goal)) {
    return []
  }

  return Array.from(
    new Set(
      profile.my_daily_goal.filter(
        (weekday): weekday is string =>
          typeof weekday === 'string' && weekday.trim().length > 0,
      ),
    ),
  )
}

export function getWeekdayValue(date: Date) {
  return WEEKDAY_VALUES[date.getDay()]
}

export function clampPercent(percent: number) {
  if (!Number.isFinite(percent)) {
    return 0
  }

  return Math.min(100, Math.max(0, percent))
}

export function getCurrentWeekBounds(date = new Date()): WeekBounds {
  const startInclusive = getLocalDayStart(date)
  const currentDay = startInclusive.getDay()
  const dayOffset = currentDay === 0 ? -6 : MONDAY_WEEK_START - currentDay
  startInclusive.setDate(startInclusive.getDate() + dayOffset)

  const endExclusive = new Date(startInclusive)
  endExclusive.setDate(endExclusive.getDate() + 7)

  return { endExclusive, startInclusive }
}

export function getStudiedSecondsBetween({
  endExclusive,
  profile,
  startInclusive,
}: {
  readonly endExclusive: Date
  readonly profile: LearningActivityProfileSource | null
  readonly startInclusive: Date
}) {
  const startTime = startInclusive.getTime()
  const endTime = endExclusive.getTime()

  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
    return 0
  }

  return getProfileActivities(profile).reduce((totalSeconds, activity) => {
    const createdAt = getActivityCreatedAt(activity)

    if (!createdAt || createdAt < startInclusive || createdAt >= endExclusive) {
      return totalSeconds
    }

    return totalSeconds + getActivitySeconds(activity)
  }, 0)
}

export function getDailyStudiedSeconds(
  profile: LearningActivityProfileSource | null,
  date = new Date(),
) {
  return getStudiedSecondsBetween({
    endExclusive: getNextLocalDayStart(date),
    profile,
    startInclusive: getLocalDayStart(date),
  })
}

export function getWeeklyStudiedSeconds(
  profile: LearningActivityProfileSource | null,
  date = new Date(),
) {
  return getStudiedSecondsBetween({
    ...getCurrentWeekBounds(date),
    profile,
  })
}

export function getWeeklyStudiedSecondsForWeekday(
  profile: LearningActivityProfileSource | null,
  weekdayValue: string,
  date = new Date(),
) {
  const weekBounds = getCurrentWeekBounds(date)

  return getProfileActivities(profile).reduce((totalSeconds, activity) => {
    const createdAt = getActivityCreatedAt(activity)

    if (
      !createdAt ||
      createdAt < weekBounds.startInclusive ||
      createdAt >= weekBounds.endExclusive ||
      getWeekdayValue(createdAt) !== weekdayValue
    ) {
      return totalSeconds
    }

    return totalSeconds + getActivitySeconds(activity)
  }, 0)
}

export function getWeeklyGoalTargetSeconds(profile: LearningActivityProfileSource | null) {
  return getSelectedGoalDays(profile).length * DAILY_GOAL_SECONDS
}

export function getDailyGoalTargetSeconds(
  profile: LearningActivityProfileSource | null,
  date = new Date(),
) {
  const selectedGoalDays = getSelectedGoalDays(profile)

  if (selectedGoalDays.length === 0) {
    return 0
  }

  return selectedGoalDays.includes(getWeekdayValue(date)) ? DAILY_GOAL_SECONDS : 0
}

export function getDisplayMinutesFromSeconds(seconds: number) {
  if (!Number.isFinite(seconds)) {
    return 0
  }

  return Math.floor(Math.max(0, seconds) / 60)
}

export function getDisplayMinutesFromSecondsMinOne(seconds: number) {
  if (!Number.isFinite(seconds)) {
    return 0
  }

  const safeSeconds = Math.max(0, seconds)

  if (safeSeconds <= 0) {
    return 0
  }

  return Math.max(1, Math.floor(safeSeconds / 60))
}

export function getWeeklyGoalProgressPercent(
  profile: LearningActivityProfileSource | null,
  date = new Date(),
) {
  const targetSeconds = getWeeklyGoalTargetSeconds(profile)

  if (targetSeconds <= 0) {
    return 0
  }

  return clampPercent((getWeeklyStudiedSeconds(profile, date) / targetSeconds) * 100)
}

export function getLearningActivityMetrics(
  profile: LearningActivityProfileSource | null,
  date = new Date(),
): LearningActivityMetrics {
  return {
    dailyStudiedSeconds: getDailyStudiedSeconds(profile, date),
    todayTargetSeconds: getDailyGoalTargetSeconds(profile, date),
    weeklyGoalProgressPercent: getWeeklyGoalProgressPercent(profile, date),
    weeklyStudiedSeconds: getWeeklyStudiedSeconds(profile, date),
    weeklyTargetSeconds: getWeeklyGoalTargetSeconds(profile),
  }
}

export function getTodayLearningMinutes(profile: LearningActivityProfileSource | null) {
  return getDisplayMinutesFromSeconds(getDailyStudiedSeconds(profile))
}